import { SelectItemV2, Unit, UnitsV2 } from '@schenck/rotec-components';
import { LocationData } from 'src/components/modals/add-edit-location-modal/add-edit-location-modal.component';
import {
  AXIAL_CORRECTION_ORIENTATIONS,
  CIRCULAR_CORRECTION_ORIENTATIONS,
  CorrectionDrillTipsTypes,
  CORRECTION_DIRECTIONS,
  RADIAL_CORRECTION_ORIENTATIONS,
  MaterialDensityEnum,
} from 'src/pages/rotor-type-details/steps/correction-method-step/correction-method.constants';
import { SI_UNITS_INDEX, SI_UNITS_SYMBOLS } from 'src/utils/constants/units.const';
import {
  CorrectionDrillTips,
  GeometryTypes,
  RotorTypeCreationRequestTypes,
} from 'src/utils/enums/rotor-type.enum';
import { UnitFamily, UnitTypes } from 'src/utils/enums/units.enum';
import {
  CorrectionFormData,
  CorrectionLocationsFormData,
  SectorData,
} from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import {
  CorrectionDistribution,
  CorrectionGeometryPlane,
  CorrectionLocation,
  CorrectionPlane,
  CorrectionSector,
  RotorTypeValue,
  RotorTypeValueV2,
} from 'src/utils/interfaces/rotor-type.interfaces';

/**
 * It uses for mapping data to the RotorTypeValue type
 *
 * @param data: SelectItemV2,
 * @param units: UnitsV2[]
 * @returns RotorTypeValue
 */
export const mapToRotorTypeValue = (
  { name, value, unit, subname }: SelectItemV2,
  units: UnitsV2[]
): RotorTypeValue => {
  const type: UnitsV2 = units.find((group) => group.units.find((_unit) => _unit.name === unit));
  const siUnit = type ? SI_UNITS_SYMBOLS[type.group] : null;
  const orgUnitIndex = type ? type.units.findIndex(({ name }) => name === unit) : null;
  const siUnitIndex = type ? SI_UNITS_INDEX[type.group] : null;

  return value || value === 0
    ? {
        name,
        value: {
          siValue: +subname || +value,
          siUnit: {
            symbol: siUnit,
            unitIndex: siUnitIndex || 0,
          },
          categoryName: unit ? setCategoryName(units, unit as Unit) : 0,
          orgUnit: {
            symbol: unit as Unit,
            unitIndex: orgUnitIndex || 0,
          },
        },
        isEdited: true,
      }
    : null;
};

/**
 * It uses for set categoryName
 *
 * @param units: UnitsV2[],
 * @param unit: string
 * @returns number
 */
export const setCategoryName = (units: UnitsV2[], unit: Unit): number => {
  const unitType = units.find(({ units }) => units.find(({ name }) => name === unit)).group;

  return UnitTypes[unitType];
};

/**
 * It uses for mapping data to the RotorTypeValueV2 type
 *
 * @param data: SelectItemV2,
 * @param isUnitFamily: boolean
 * @returns RotorTypeValueV2
 */
export const mapToRotorTypeValueV2 = <T = number>(
  { name, value }: SelectItemV2,
  isUnitFamily?: boolean
): RotorTypeValueV2<T | number> =>
  value || value === 0
    ? {
        name,
        value: isUnitFamily ? +UnitFamily[`${value}`] : +value,
        isEdited: true,
      }
    : null;

/**
 * It uses for mapping data to the TolerancesGeometryPlane type
 *
 * @param data: [string, SelectItemV2][],
 * @param units: UnitsV2[]
 * @returns TolerancesGeometryPlane
 */
export const mapToCorrectionGeometryPlane = (
  data: [string, SelectItemV2][],
  units: UnitsV2[]
): CorrectionGeometryPlane => {
  return data.reduce(
    (acc, [name, value]) => ({
      ...acc,
      [GeometryTypes[name]]:
        name === GeometryTypes.supportCase
          ? mapToRotorTypeValueV2(value)
          : mapToRotorTypeValue(value, units),
    }),
    {}
  );
};

/**
 * It uses for mapping data to the CorrectionPlane type
 *
 * @param data: CorrectionFormData
 * @param index: number
 * @returns CorrectionPlane
 */
export const mapMassCorrectionPlane = (
  item: CorrectionFormData,
  index: number
): CorrectionPlane => ({
  planeIndex: index,
  correctionMethod: {
    $type: RotorTypeCreationRequestTypes.AddMassMethod,
    addMass: {
      name: 'addMass',
      value: !(item.settings - 1),
      isEdited: true,
    },
  },
});

/**
 * It uses for mapping data to the CorrectionPlane type
 *
 * @param data: CorrectionFormData
 * @param index: number
 * @param units: UnitsV2[]
 * @returns CorrectionPlane
 */
export const mapDrillCorrectionPlane = (
  item: CorrectionFormData,
  index: number,
  units: UnitsV2[]
): CorrectionPlane => ({
  planeIndex: index,
  correctionMethod: {
    $type: RotorTypeCreationRequestTypes.DrillMethod,
    drillTip: {
      $type: CorrectionDrillTipsTypes[item?.drillTip - 1]?.name,
      centerTipRadius: item?.centerTipRadius.value
        ? mapToRotorTypeValue(item.centerTipRadius, units)
        : null,
      centerTipHeight: item?.centerTipHeight.value
        ? mapToRotorTypeValue(item.centerTipHeight, units)
        : null,
      drillTipAngle:
        item?.drillTip !== CorrectionDrillTips.Cylinder
          ? ({
              name: CorrectionDrillTipsTypes[item?.drillTip - 1]?.drillTipAngle.type,
              value:
                item.drillTip === CorrectionDrillTips.Custom
                  ? item.drillTipAngle
                  : CorrectionDrillTipsTypes[item?.drillTip - 1]?.drillTipAngle.value,
              isEdited: true,
            } as any)
          : null,
      crossEdgeFactor:
        item?.drillTip !== CorrectionDrillTips.Cylinder
          ? ({
              isEdited: true,
              value:
                item.drillTip === CorrectionDrillTips.Custom
                  ? item.crossEdgeFactor
                  : CorrectionDrillTipsTypes[item?.drillTip - 1]?.crossEdgeFactor,
            } as any)
          : null,
    },
    selectedDrillTip: {
      name: CorrectionDrillTipsTypes[item?.drillTip - 1]?.type,
      value: CorrectionDrillTipsTypes[item?.drillTip - 1]?.type,
      isEdited: true,
    },
    drillDiameter: item?.drillDiameter ? mapToRotorTypeValue(item.drillDiameter, units) : null,
    correctionDirection: CORRECTION_DIRECTIONS[item?.drillDirectionSelection - 1],
    minimumHoleAngleDistance: item?.minimumDrillHoleAngleDist
      ? mapToRotorTypeValueV2(item.minimumDrillHoleAngleDist)
      : null,
    maximumDrillDepth: item.maximumDrillDepth
      ? mapToRotorTypeValue(item.maximumDrillDepth, units)
      : null,
    material: item?.materialDensity
      ? {
          name: 'materialDensity',
          density: mapToRotorTypeValue(item.materialDensity, units),
          materialType: {
            value:
              getDensityName(item.materialDensity.name) !== 'Custom'
                ? MaterialDensityEnum[getDensityName(item.materialDensity.name)]
                : 0,
            isEdited: true,
          },
        }
      : null,
    radialCorrectionOrientation: item?.drillDirectionSelection
      ? mapToRotorTypeValueV2(RADIAL_CORRECTION_ORIENTATIONS[item.drillDirectionSelection - 1])
      : null,
    axialCorrectionOrientation: item.drillDirectionSelection
      ? mapToRotorTypeValueV2(AXIAL_CORRECTION_ORIENTATIONS[item.drillDirectionSelection - 1])
      : null,
    circularCorrectionOrientation: item.drillDirectionSelection
      ? mapToRotorTypeValueV2(CIRCULAR_CORRECTION_ORIENTATIONS[item.drillDirectionSelection - 1])
      : null,
  },
});

/**
 * It uses for mapping data to the CorrectionPlane type
 *
 * @param data: CorrectionFormData
 * @param index: number
 * @param units: UnitsV2[]
 * @returns CorrectionPlane
 */
export const mapWeightSetCorrectionPlane = (
  item: CorrectionFormData,
  index: number,
  units: UnitsV2[]
): CorrectionPlane => ({
  planeIndex: index,
  correctionMethod: {
    $type: RotorTypeCreationRequestTypes.WeightSet,
    minAngleDistance: {
      name: 'minAngleDistance',
      value: item.weightsForm.correctionSpacing,
      isValid: true,
      isEdited: true,
    },
    maximumNumberOfWeightsPerLocation: {
      name: 'maximumNumberOfWeightsPerLocation',
      value: item.weightsForm.weightsPerLocation,
      isValid: true,
      isEdited: true,
    },
    displayMode: {
      name: 'displayMode',
      value: item.weightsForm.balancingDisplay,
      isEdited: true,
    },
    numberOfWeights: {
      value: item?.weightsForm?.weights?.length, // int
      isEdited: true,
    },
    addMass: {
      name: 'addMass',
      value: item.weightsForm.addMass,
      isEdited: true,
    },
    weights: item.weightsForm.weights,
  },
});

export const mapToCorrectionDistribution = (
  data: CorrectionLocationsFormData,
  units: UnitsV2[]
): CorrectionDistribution => {
  let type: RotorTypeCreationRequestTypes;

  if (data.locationType === 'Free') {
    type = RotorTypeCreationRequestTypes.LocationFree;
  } else if (data.locationType === 'Fixed') {
    type = RotorTypeCreationRequestTypes.LocationFixed;
  } else {
    type = RotorTypeCreationRequestTypes.LocationSectors;
  }

  return {
    $type: type,
    ...(data.locationType === 'Fixed' && {
      locations: [...data.locations]
        .sort((a, b) => a.angle - b.angle)
        .map((location: LocationData, i): CorrectionLocation => {
          return {
            locationIndex: i,
            location: {
              value: location.angle,
              name: `${location.side}-location-${location.angle}`,
              isEdited: true,
            },
            correctionRadius: location.correctionRadius?.value
              ? mapToRotorTypeValue(
                  {
                    name: `${location.side}-radius-${location.correctionRadius.value}`,
                    value: location.correctionRadius.value,
                    unit: location.correctionRadius.unit,
                  },
                  units
                )
              : null,
            restricted: {
              value: location.restricted,
              name: `${location.side}-restriction-${location.angle}`,
              isEdited: true,
            },
          };
        }),
      ...(data.locationType === 'Fixed' && {
        maximumNumberOfCorrectionsPerStep: {
          name: 'maximumNumberOfCorrectionsPerStep',
          value: data.locations?.filter((i) => !i.restricted).length,
          isEdited: true,
        },
        numberOfLocations: {
          name: 'numberOfLocations',
          value: data.locations?.length,
          isEdited: true,
        },
        hasRadiusPerPlace: {
          name: 'hasRadiusPerPlace',
          value: data.hasRadiusPerPlace || false,
          isEdited: true,
        },
      }),
    }),
    ...(data.locationType === 'Sector' && {
      numberOfSectors: {
        value: data.sectors.length,
        isEdited: true,
      },
      sectors: [...data.sectors]
        .sort((a, b) => a.sectorIndex - b.sectorIndex)
        .map((sector: SectorData, i): CorrectionSector => {
          return {
            sectorIndex: i,
            startAngle: {
              value: sector.startAngle,
              name: `restriction-${sector.startAngle}`,
              isEdited: true,
            },
            endAngle: {
              value: sector.endAngle,
              name: `restriction-${sector.endAngle}`,
              isEdited: true,
            },
            correctionRadius: mapToRotorTypeValue(sector.correctionRadius, units),
            restricted: {
              value: sector.restricted,
              name: `restriction-${sector.restricted}`,
              isEdited: true,
            },
          };
        }),
      ...(data.locationType === 'Sector' && {
        maximumNumberOfCorrectionsPerStep: {
          name: 'maximumNumberOfCorrectionsPerStep',
          value: 10,
          isEdited: true,
        },
        maximumCorrectionAngleRange: {
          name: 'maximumCorrectionAngleRange',
          value: 19,
          isEdited: true,
        },
        hasRadiusPerPlace: {
          name: 'hasRadiusPerPlace',
          value: data.hasRadiusPerPlace || false,
          isEdited: true,
        },
      }),
    }),
  };
};

export const getDensityName = (name: string): string => {
  if (name.substring(0, 4) === 'left') {
    return name.replace('left', '');
  } else {
    return name.replace('right', '');
  }
};
